// The page-id → route table behind "where do we land this user?".
//
// Page access is granted per store as `allowed_pages_json[shopKey]` — an object
// of { "<Page Name>": <numeric id> } whose VALUES are what the client checks
// (see Sidebar.jsx). Until now the id → route half of that story existed only
// inline in Sidebar.jsx's JSX, so the store switcher and the login flow both
// hardcoded /campaign-summary and stranded users without page 28 on a screen
// they cannot see.
//
// PAGE_ROUTES lists the ROUTABLE sidebar destinations in sidebar render order —
// deliberately a fixed order, because Object.values() over the cookie object
// follows the Mongo document's arbitrary key order and "first page the user has
// access to" must not vary by store. Ids with no route of their own (Settings
// tabs 4/17/21/22/23/26/32/42/44/45/56/103, Category-internal 19/27, 37) are
// deliberately absent; hidden sidebar entries (35 Media Section) too. Id 51 is
// double-booked historically (hidden LP Preview + CF Past Runs) — the visible
// Cognitive Flows meaning wins here.
//
// Keep in sidebar order when adding entries — Sidebar.jsx remains the visual
// source of truth for grouping.
export const PAGE_ROUTES = [
  // Campaigns
  { id: 28, path: '/campaign-summary' },
  { id: 31, path: '/ai-mode-lp-customization' },
  // Sections
  { id: 25, path: '/imported-sections' },
  { id: 24, path: '/chatbot-suggestions-generation' },
  { id: 33, path: '/saved-sections' },
  { id: 34, path: '/ai-asset-content-generator' },
  { id: 58, path: '/enhance-sections' },
  { id: 59, path: '/new-sections' },
  { id: 60, path: '/imported-forms' },
  // Analytics
  { id: 36, path: '/analytics-dashboard' },
  { id: 41, path: '/opportunities' },
  { id: 38, path: '/keyword-lp-relevance' },
  { id: 20, path: '/chatbot' },
  // Others
  { id: 10, path: '/ads-copy-optimization' },
  { id: 15, path: '/google-ads-copy-ss' },
  { id: 16, path: '/creative-analysis' },
  { id: 14, path: '/creative_ideation' },
  { id: 18, path: '/reports' },
  { id: 39, path: '/bulk-variation-upload' },
  { id: 12, path: '/all-product-audit' },
  { id: 9, path: '/product-cro' },
  { id: 11, path: '/segment-bestseller' },
  { id: 2, path: '/lifetime-value' },
  { id: 5, path: '/enrich-and-bid' },
  { id: 7, path: '/feed-enrichment' },
  { id: 13, path: '/shopping' },
  { id: 0, path: '/metrics-performance' },
  { id: 1, path: '/metrics-deepdive' },
  { id: 3, path: '/product-journey' },
  { id: 6, path: '/product-performance' },
  { id: 8, path: '/repurchase-rate' },
  // Workflows
  { id: 62, path: '/cognitive-flows/agents' },
  { id: 63, path: '/cognitive-flows/library' },
  { id: 46, path: '/cognitive-flows' },
  // Reports
  { id: 65, path: '/cognitive-flows/canvas' },
  { id: 47, path: '/cognitive-flows/chat' },
  // Context
  { id: 49, path: '/cognitive-flows/knowledge' },
  { id: 48, path: '/cognitive-flows/data-sources' },
  { id: 64, path: '/cognitive-flows/data-sinks' },
  // Workspace Settings
  { id: 61, path: '/cognitive-flows/setup' },
  { id: 50, path: '/cognitive-flows/runs' },
  { id: 51, path: '/cognitive-flows/past-runs' },
];

// Preferred landing pages, in order: Campaign Summary, then the Agents page
// ("My Workflows", id 62), then the workflow Builder (46). Only after those
// does sidebar order decide.
const LANDING_PRIORITY = [28, 62, 46];

const PATH_BY_ID = Object.fromEntries(PAGE_ROUTES.map((p) => [p.id, p.path]));

// Account Settings is rendered for every user regardless of allowed_pages
// (Sidebar.jsx keeps that group display:block), so it is the safe last resort.
const FALLBACK_PATH = '/settings';

/**
 * The route a user should land on after login or a store switch, given the
 * page map for THAT store (`allowed_pages_json[shopKey]` — name → id object,
 * or anything falsy/malformed).
 */
/**
 * Where to land someone who has just finished account setup.
 *
 * Setup ends on "choose a plan", and the thing they came to build is a
 * workflow — so the destination is the Workflows group, not `resolveLandingPage`'s
 * priority list, which puts Campaign Summary first and drops a fresh
 * Cognitive Agents account on the integrations page it happens to grant.
 *
 * Builder first, then the Agents list, then the library: a plan need not grant
 * all three. Starter grants 62 and 63 but NOT 46, so hardcoding the builder
 * would send its subscribers to a page they cannot open. Falls back to the
 * normal landing rules when a plan grants no Workflows page at all.
 */
export function resolveWorkflowsLandingPage(allowedPagesForShop) {
  const granted = new Set(
    Object.values(allowedPagesForShop || {})
      .map(Number)
      .filter(Number.isFinite),
  );
  for (const id of [46, 62, 63]) {
    if (granted.has(id)) return PATH_BY_ID[id];
  }
  return resolveLandingPage(allowedPagesForShop);
}

export function resolveLandingPage(allowedPagesForShop) {
  const granted = new Set(
    Object.values(allowedPagesForShop || {})
      .map(Number)
      .filter(Number.isFinite),
  );
  for (const id of LANDING_PRIORITY) {
    if (granted.has(id)) return PATH_BY_ID[id];
  }
  for (const { id, path } of PAGE_ROUTES) {
    if (granted.has(id)) return path;
  }
  return FALLBACK_PATH;
}

/**
 * Where a customer who has run out of credits is sent to upgrade.
 *
 * A Settings TAB rather than a route of its own — page 22, one of the tab ids
 * the note at the top explains are deliberately absent from PAGE_ROUTES — so it
 * is a query string, not a path.
 *
 * Named here rather than typed at each call site because the upgrade prompt has
 * two entry points: the out-of-credits modal shown over canvas, chat and the
 * workflow editor, and the Billing & Payment note that replaces Top Up on a
 * free plan. Moving the destination and fixing only one of them would strand a
 * customer mid-upgrade.
 */
export const UPGRADE_PLAN_ROUTE = '/settings?tab=saving-calculator';
